import { useState, useRef, useEffect, useCallback } from 'react'
import type { EventConfig, PrintLayoutSlot } from '@/types'
import { PAPER_SIZES, DPI_PRESETS } from '@/lib/constants'
import { generateId, uploadFile } from '@/lib/utils'
import {
  Camera, Type, Square, QrCode, ImageIcon, Upload, Trash2,
  ChevronUp, ChevronDown, Eye, Palette, Move,
  AlignVerticalJustifyStart, AlignVerticalJustifyCenter, AlignVerticalJustifyEnd,
  AlignHorizontalJustifyStart, AlignHorizontalJustifyCenter, AlignHorizontalJustifyEnd,
  RotateCcw,
} from 'lucide-react'

const inputCls = 'w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm text-foreground focus:border-primary/50 transition-colors'

const ADD_TOOLS: { type: PrintLayoutSlot['type']; label: string; icon: React.ElementType; desc: string }[] = [
  { type: 'photo', label: 'Photo From Booth', icon: Camera, desc: 'Photo slot' },
  { type: 'image', label: 'Image', icon: ImageIcon, desc: 'Static image' },
  { type: 'text', label: 'Text', icon: Type, desc: 'Text label' },
  { type: 'shape', label: 'Shape', icon: Square, desc: 'Rectangle/circle' },
  { type: 'qr', label: 'QR Code', icon: QrCode, desc: 'QR code' },
]

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <div className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">{label}</div>
      {children}
    </div>
  )
}

import { useI18n } from '@/lib/i18n'

export default function PrintLayoutEditor({ event, onChange }: { event: EventConfig; onChange: (u: Partial<EventConfig>) => void }) {
  const { t } = useI18n()
  const layout = event.printLayout
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [dragging, setDragging] = useState<{ id: string; offsetX: number; offsetY: number } | null>(null)
  const [resizing, setResizing] = useState<{ id: string; startW: number; startH: number; mouseX: number; mouseY: number } | null>(null)
  const canvasRef = useRef<HTMLDivElement>(null)

  const updateLayout = useCallback((updates: Partial<typeof layout>) => {
    onChange({ printLayout: { ...layout, ...updates } })
  }, [layout, onChange])

  const addSlot = (type: PrintLayoutSlot['type']) => {
    const slot: PrintLayoutSlot = {
      id: generateId(), type,
      x: 50, y: 50,
      width: type === 'photo' ? 400 : type === 'text' ? 300 : type === 'qr' ? 150 : 200,
      height: type === 'photo' ? 300 : type === 'text' ? 60 : type === 'qr' ? 150 : 200,
      rotation: 0, zIndex: layout.slots.length,
      content: type === 'text' ? 'Text' : undefined,
      photoIndex: type === 'photo' ? layout.slots.filter((s) => s.type === 'photo').length : undefined,
    }
    updateLayout({ slots: [...layout.slots, slot] })
    setSelectedId(slot.id)
  }

  const updateSlot = useCallback((id: string, updates: Partial<PrintLayoutSlot>) => {
    updateLayout({ slots: layout.slots.map((s) => (s.id === id ? { ...s, ...updates } : s)) })
  }, [layout.slots, updateLayout])

  const deleteSlot = (id: string) => {
    updateLayout({ slots: layout.slots.filter((s) => s.id !== id) })
    setSelectedId(null)
  }

  const selected = layout.slots.find((s) => s.id === selectedId)

  const size = layout.paperSize === 'custom'
    ? { w: layout.customWidth, h: layout.customHeight }
    : { w: PAPER_SIZES[layout.paperSize].width, h: PAPER_SIZES[layout.paperSize].height }

  const maxW = Math.max(320, window.innerWidth - 680)
  const maxH = Math.max(240, window.innerHeight - 200)
  const scale = Math.min(maxW / size.w, maxH / size.h, 1)
  const displayW = Math.round(size.w * scale)
  const displayH = Math.round(size.h * scale)

  // Drag & resize handlers
  const onMouseDown = (e: React.MouseEvent, slot: PrintLayoutSlot) => {
    e.stopPropagation()
    setSelectedId(slot.id)
    const rect = canvasRef.current!.getBoundingClientRect()
    setDragging({ id: slot.id, offsetX: (e.clientX - rect.left) / scale - slot.x, offsetY: (e.clientY - rect.top) / scale - slot.y })
  }

  const onResizeMouseDown = (e: React.MouseEvent, slot: PrintLayoutSlot) => {
    e.stopPropagation()
    setResizing({ id: slot.id, startW: slot.width, startH: slot.height, mouseX: e.clientX, mouseY: e.clientY })
  }

  useEffect(() => {
    const handleMove = (e: MouseEvent) => {
      if (dragging) {
        const rect = canvasRef.current!.getBoundingClientRect()
        updateSlot(dragging.id, {
          x: Math.round((e.clientX - rect.left) / scale - dragging.offsetX),
          y: Math.round((e.clientY - rect.top) / scale - dragging.offsetY),
        })
      }
      if (resizing) {
        updateSlot(resizing.id, {
          width: Math.max(20, Math.round(resizing.startW + (e.clientX - resizing.mouseX) / scale)),
          height: Math.max(20, Math.round(resizing.startH + (e.clientY - resizing.mouseY) / scale)),
        })
      }
    }
    const handleUp = () => { setDragging(null); setResizing(null) }
    window.addEventListener('mousemove', handleMove)
    window.addEventListener('mouseup', handleUp)
    return () => { window.removeEventListener('mousemove', handleMove); window.removeEventListener('mouseup', handleUp) }
  }, [dragging, resizing, scale, updateSlot])

  // Delete key
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedId && !(e.target instanceof HTMLInputElement)) deleteSlot(selectedId)
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [selectedId])

  // Alignment helpers
  const alignSlot = (axis: 'left' | 'centerH' | 'right' | 'top' | 'centerV' | 'bottom') => {
    if (!selected) return
    const map: Record<string, Partial<PrintLayoutSlot>> = {
      left: { x: 0 }, centerH: { x: Math.round((size.w - selected.width) / 2) }, right: { x: size.w - selected.width },
      top: { y: 0 }, centerV: { y: Math.round((size.h - selected.height) / 2) }, bottom: { y: size.h - selected.height },
    }
    updateSlot(selected.id, map[axis])
  }

  const sortedSlots = [...layout.slots].sort((a, b) => a.zIndex - b.zIndex)

  return (
    <div className="flex h-full gap-0 overflow-hidden -m-6">
      {/* LEFT PANEL, Add & Layout */}
      <div className="flex w-56 shrink-0 flex-col border-r border-border bg-card/50 overflow-auto p-3 gap-5">
        {/* Add section */}
        <div className="space-y-1">
          <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60 px-1">{t('print.add')}</div>
          {ADD_TOOLS.map(({ type, icon: Icon }) => (
            <button key={type} onClick={() => addSlot(type)}
              className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-colors">
              <Icon className="h-4 w-4 shrink-0" />
              {t(`print.${type === 'photo' ? 'addPhotoSlot' : type === 'text' ? 'addText' : type === 'shape' ? 'addShape' : type === 'qr' ? 'addQR' : 'addImage'}`)}
            </button>
          ))}
        </div>

        {/* Background color */}
        <div className="space-y-1.5">
          <div className="flex items-center gap-2 px-1">
            <Palette className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">{t('print.backgroundColor')}</span>
          </div>
          <div className="flex items-center gap-2">
            <label className="relative cursor-pointer">
              <div className="h-8 w-8 rounded-lg border border-border" style={{ backgroundColor: layout.backgroundColor }} />
              <input type="color" value={layout.backgroundColor} onChange={(e) => updateLayout({ backgroundColor: e.target.value })} className="absolute inset-0 opacity-0 w-full h-full cursor-pointer" />
            </label>
            <input type="text" value={layout.backgroundColor} onChange={(e) => updateLayout({ backgroundColor: e.target.value })} className="flex-1 rounded-md border border-input bg-background px-2 py-1 text-xs font-mono" />
          </div>
          {/* Background image */}
          <label className="flex items-center gap-2 rounded-md border border-dashed border-border bg-background px-2 py-1.5 cursor-pointer hover:border-primary/40 hover:bg-muted transition-colors">
            <Upload className="h-3 w-3 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">{layout.backgroundImage ? 'Change bg image' : 'Upload bg image'}</span>
            <input type="file" accept="image/*" className="hidden" onChange={async (e) => { 
              const f = e.target.files?.[0]; 
              if (f) {
                const url = await uploadFile(f)
                if (url) updateLayout({ backgroundImage: url })
              }
            }} />
          </label>
          {layout.backgroundImage && (
            <button onClick={() => updateLayout({ backgroundImage: null })} className="text-xs text-destructive hover:underline">Remove bg image</button>
          )}
          {/* Overlay image */}
          <label className="flex items-center gap-2 rounded-md border border-dashed border-border bg-background px-2 py-1.5 cursor-pointer hover:border-primary/40 hover:bg-muted transition-colors mt-2">
            <Upload className="h-3 w-3 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">{layout.overlayImage ? 'Change overlay image' : 'Upload overlay image (foreground)'}</span>
            <input type="file" accept="image/*" className="hidden" onChange={async (e) => { 
              const f = e.target.files?.[0]; 
              if (f) {
                const url = await uploadFile(f)
                if (url) updateLayout({ overlayImage: url })
              }
            }} />
          </label>
          {layout.overlayImage && (
            <button onClick={() => updateLayout({ overlayImage: null })} className="text-xs text-destructive hover:underline">Remove overlay</button>
          )}
        </div>

        {/* Layout settings */}
        <div className="space-y-3">
          <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60 px-1">{t('print.layout')}</div>
          <Field label={t('print.paperSize')}>
            <select value={layout.paperSize} onChange={(e) => updateLayout({ paperSize: e.target.value as keyof typeof PAPER_SIZES })} className={inputCls}>
              {Object.entries(PAPER_SIZES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </Field>
          {layout.paperSize === 'custom' && (
            <div className="grid grid-cols-2 gap-2">
              <Field label={t('screen.width')}><input type="number" value={layout.customWidth} onChange={(e) => updateLayout({ customWidth: Number(e.target.value) })} className={inputCls} /></Field>
              <Field label={t('screen.height')}><input type="number" value={layout.customHeight} onChange={(e) => updateLayout({ customHeight: Number(e.target.value) })} className={inputCls} /></Field>
            </div>
          )}
          <Field label={t('print.resolution')}>
            <select value={layout.dpi} onChange={(e) => updateLayout({ dpi: Number(e.target.value) })} className={inputCls}>
              {DPI_PRESETS.map((d) => <option key={d} value={d}>{d} DPI</option>)}
            </select>
          </Field>
          <div className="text-[10px] text-muted-foreground/50 px-1">{size.w}×{size.h}px at {layout.dpi} DPI</div>
        </div>
      </div>

      {/* CENTER, Canvas */}
      <div className="flex flex-1 flex-col items-center justify-start overflow-auto bg-[#0b0b0a] p-6 gap-3">
        <div className="text-xs text-muted-foreground/50">Print Layout · {Math.round(scale * 100)}% · Drag to position · Del to remove</div>
        <div className="relative overflow-hidden rounded-lg border border-white/10 shadow-md"
          style={{ width: displayW, height: displayH, backgroundColor: layout.backgroundColor }}>
          <div ref={canvasRef} className="absolute inset-0 origin-top-left"
            style={{ width: size.w, height: size.h, transform: `scale(${scale})` }}
            onClick={() => setSelectedId(null)}>
            {layout.backgroundImage && <img src={layout.backgroundImage} className="absolute inset-0 h-full w-full object-cover" alt="" />}
            {sortedSlots.map((slot) => (
              <div key={slot.id} onMouseDown={(e) => onMouseDown(e, slot)} onClick={(e) => e.stopPropagation()}
                className="absolute group"
                style={{ left: slot.x, top: slot.y, width: slot.width, height: slot.height, transform: `rotate(${slot.rotation}deg)`, zIndex: slot.zIndex, cursor: dragging?.id === slot.id ? 'grabbing' : 'grab' }}>
                <div className={`h-full w-full flex items-center justify-center overflow-hidden ${selectedId === slot.id ? 'ring-2 ring-primary ring-offset-1 ring-offset-transparent' : 'group-hover:ring-1 group-hover:ring-white/30'}`}
                  style={{ backgroundColor: slot.type === 'shape' ? (slot.backgroundColor || '#ccc') : slot.backgroundColor, borderRadius: slot.borderRadius || 0, color: slot.color, fontSize: slot.fontSize || 16, fontFamily: slot.fontFamily }}>
                  {slot.type === 'photo' && (
                    <div className="flex h-full w-full flex-col items-center justify-center border-2 border-dashed border-primary/40 bg-primary/10">
                      <Camera className="h-6 w-6 text-primary/60 mb-1" />
                      <span className="text-primary/80 text-lg font-bold">{(slot.photoIndex ?? 0) + 1}</span>
                    </div>
                  )}
                  {slot.type === 'text' && <span className="px-2" style={{ textAlign: slot.align }}>{slot.content}</span>}
                  {slot.type === 'qr' && (
                    <div className="flex h-full w-full items-center justify-center border-2 border-dashed border-amber-400/40 bg-amber-500/10">
                      <QrCode className="h-6 w-6 text-amber-400/60" />
                    </div>
                  )}
                  {slot.type === 'image' && slot.src && <img src={slot.src} alt="" className="h-full w-full object-cover" />}
                  {slot.type === 'image' && !slot.src && (
                    <div className="flex h-full w-full items-center justify-center border-2 border-dashed border-border bg-secondary">
                      <ImageIcon className="h-6 w-6 text-muted-foreground" />
                    </div>
                  )}
                </div>
                {selectedId === slot.id && (
                  <div onMouseDown={(e) => onResizeMouseDown(e, slot)} className="absolute -bottom-2 -right-2 h-4 w-4 cursor-se-resize rounded-full border-2 border-white bg-primary shadow-lg" />
                )}
              </div>
            ))}
            {layout.overlayImage && <img src={layout.overlayImage} className="absolute inset-0 h-full w-full object-cover pointer-events-none z-[100]" alt="" />}
          </div>
        </div>
        {/* Bottom bar, Bring Forward / Send Backward */}
        <div className="flex items-center gap-4">
          <button onClick={() => selected && updateSlot(selected.id, { zIndex: selected.zIndex + 1 })}
            disabled={!selected}
            className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs text-muted-foreground hover:bg-white/5 disabled:opacity-30 transition-colors">
            <ChevronUp className="h-3.5 w-3.5" /> {t('screen.bringForward')}
          </button>
          <button onClick={() => selected && updateSlot(selected.id, { zIndex: Math.max(0, selected.zIndex - 1) })}
            disabled={!selected}
            className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs text-muted-foreground hover:bg-white/5 disabled:opacity-30 transition-colors">
            <ChevronDown className="h-3.5 w-3.5" /> {t('screen.sendBackward')}
          </button>
        </div>
      </div>

      {/* RIGHT PANEL, Selected & Layers */}
      <div className="flex w-64 shrink-0 flex-col border-l border-border bg-card/50 overflow-auto">
        {selected ? (
          <div className="p-3 space-y-4">
            {/* Selected header */}
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('screen.selected')}</span>
              <button onClick={() => deleteSlot(selected.id)} className="rounded-md p-1 hover:bg-destructive/15 text-muted-foreground hover:text-destructive transition-colors" title={t('common.delete')}>
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>

            {/* Position & Size */}
            <div className="grid grid-cols-2 gap-2">
              <Field label="X"><input type="number" value={Math.round(selected.x)} onChange={(e) => updateSlot(selected.id, { x: Number(e.target.value) })} className={inputCls} /></Field>
              <Field label="Y"><input type="number" value={Math.round(selected.y)} onChange={(e) => updateSlot(selected.id, { y: Number(e.target.value) })} className={inputCls} /></Field>
              <Field label="W"><input type="number" value={selected.width} onChange={(e) => updateSlot(selected.id, { width: Number(e.target.value) })} className={inputCls} /></Field>
              <Field label="H"><input type="number" value={selected.height} onChange={(e) => updateSlot(selected.id, { height: Number(e.target.value) })} className={inputCls} /></Field>
            </div>

            {/* Rotate */}
            <div className="flex items-center gap-2">
              <RotateCcw className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <Field label="Rotate">
                <div className="flex items-center gap-1">
                  <input type="number" value={selected.rotation} onChange={(e) => updateSlot(selected.id, { rotation: Number(e.target.value) })} className={inputCls} />
                  <span className="text-xs text-muted-foreground">°</span>
                </div>
              </Field>
            </div>

            {/* Alignment */}
            <div>
              <div className="mb-1.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Alignment</div>
              <div className="grid grid-cols-6 gap-1">
                {([
                  { key: 'left' as const, icon: AlignHorizontalJustifyStart },
                  { key: 'centerH' as const, icon: AlignHorizontalJustifyCenter },
                  { key: 'right' as const, icon: AlignHorizontalJustifyEnd },
                  { key: 'top' as const, icon: AlignVerticalJustifyStart },
                  { key: 'centerV' as const, icon: AlignVerticalJustifyCenter },
                  { key: 'bottom' as const, icon: AlignVerticalJustifyEnd },
                ]).map(({ key, icon: Icon }) => (
                  <button key={key} onClick={() => alignSlot(key)}
                    className="flex h-8 items-center justify-center rounded-md border border-border bg-background text-muted-foreground hover:bg-primary/15 hover:text-primary hover:border-primary/30 transition-colors">
                    <Icon className="h-3.5 w-3.5" />
                  </button>
                ))}
              </div>
            </div>

            {/* Type-specific props */}
            {selected.type === 'text' && (
              <div className="space-y-2 border-t border-border pt-3">
                <Field label="Content"><input type="text" value={selected.content || ''} onChange={(e) => updateSlot(selected.id, { content: e.target.value })} className={inputCls} /></Field>
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Font Size"><input type="number" value={selected.fontSize || 16} onChange={(e) => updateSlot(selected.id, { fontSize: Number(e.target.value) })} className={inputCls} /></Field>
                  <Field label="Color"><input type="color" value={selected.color || '#000000'} onChange={(e) => updateSlot(selected.id, { color: e.target.value })} className="h-9 w-full rounded-md border border-input cursor-pointer" /></Field>
                </div>
                <Field label="Align">
                  <div className="flex gap-1">
                    {(['left', 'center', 'right'] as const).map((a) => (
                      <button key={a} onClick={() => updateSlot(selected.id, { align: a })}
                        className={`flex-1 rounded-md border py-1 text-xs capitalize transition-colors ${selected.align === a ? 'border-primary/40 bg-primary/10 text-primary' : 'border-border hover:bg-muted text-muted-foreground'}`}>
                        {a}
                      </button>
                    ))}
                  </div>
                </Field>
                <Field label="Background"><input type="color" value={selected.backgroundColor || '#00000000'} onChange={(e) => updateSlot(selected.id, { backgroundColor: e.target.value })} className="h-9 w-full rounded-md border border-input cursor-pointer" /></Field>
              </div>
            )}

            {selected.type === 'shape' && (
              <div className="space-y-2 border-t border-border pt-3">
                <Field label="Fill Color"><input type="color" value={selected.backgroundColor || '#cccccc'} onChange={(e) => updateSlot(selected.id, { backgroundColor: e.target.value })} className="h-9 w-full rounded-md border border-input cursor-pointer" /></Field>
                <Field label="Border Radius"><input type="number" value={selected.borderRadius || 0} onChange={(e) => updateSlot(selected.id, { borderRadius: Number(e.target.value) })} className={inputCls} /></Field>
              </div>
            )}

            {selected.type === 'image' && (
              <div className="space-y-2 border-t border-border pt-3">
                <Field label="Image URL"><input type="text" value={selected.src || ''} onChange={(e) => updateSlot(selected.id, { src: e.target.value })} className={inputCls} placeholder="https://…" /></Field>
                <label className="flex items-center gap-2 rounded-md border border-dashed border-border bg-background px-3 py-2 cursor-pointer hover:border-primary/40 hover:bg-muted transition-colors">
                  <Upload className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">Upload image</span>
                  <input type="file" accept="image/*" className="hidden" onChange={async (e) => { 
                    const f = e.target.files?.[0]; 
                    if (f) {
                      const url = await uploadFile(f)
                      if (url) updateSlot(selected.id, { src: url })
                    }
                  }} />
                </label>
              </div>
            )}

            {selected.type === 'photo' && (
              <div className="space-y-2 border-t border-border pt-3">
                <Field label={t('print.photoIndex')}>
                  <input type="number" min={0} value={selected.photoIndex ?? 0} onChange={(e) => updateSlot(selected.id, { photoIndex: Number(e.target.value) })} className={inputCls} />
                </Field>
                <div className="text-[10px] text-muted-foreground/60">{t('print.photoIndexDesc')}</div>
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center gap-3 p-6 text-center flex-1">
            <Move className="h-8 w-8 text-muted-foreground/30" />
            <p className="text-xs text-muted-foreground">{t('screen.clickToEdit')}</p>
          </div>
        )}

        {/* Layers panel, always visible at bottom */}
        <div className="mt-auto border-t border-border p-3 space-y-1">
          <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60">{t('screen.layers')}</div>
          {layout.slots.length === 0 && <div className="text-xs text-muted-foreground/50">{t('screen.noElements')}</div>}
          {[...layout.slots].sort((a, b) => b.zIndex - a.zIndex).map((slot) => {
            const tool = ADD_TOOLS.find(t => t.type === slot.type)
            const Icon = tool?.icon ?? Move
            return (
              <div key={slot.id} onClick={() => setSelectedId(slot.id)}
                className={`flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-xs transition-colors ${selectedId === slot.id ? 'bg-primary/15 text-primary' : 'hover:bg-muted text-muted-foreground'}`}>
                <Eye className="h-3 w-3 shrink-0 opacity-40" />
                <Icon className="h-3 w-3 shrink-0" />
                <span className="truncate">{slot.type === 'photo' ? `Photo ${(slot.photoIndex ?? 0) + 1}` : slot.content || slot.type}</span>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
